import { useQuery } from "@apollo/client";
import KpisChart from "components/charts/modern/KpisChart";
import PlotTypeSelector from "components/charts/modern/inputs/PlotTypeSelector";
import SelectStatementType from "components/financials/modern/SelectStatementType";
import KpisQuery from "components/financials/modern/graphql/KpisQuery";
import type {
  IKpisQuery,
  IKpisQueryVariables,
} from "components/financials/modern/graphql/__generated__/KpisQuery.generated";
import LoadingSkeleton from "components/pages/TemplatesGalleryPage/LoadingSkeleton";
import ReactSelect from "components/ui/ReactSelect";
import { ScrollArea } from "components/ui/ScrollArea";
import classNames from "components/ui/classNames";
import lightFormat from "date-fns/lightFormat";
import parseISO from "date-fns/parseISO";
import { orderBy } from "lodash";
import { ActivityIcon, BarChartIcon, TableIcon } from "lucide-react";
import { startTransition, useState } from "react";
import { useIntl } from "react-intl";
import { IFinancialsStatementType } from "types/graphqlTypes";

interface KpisProps {
  tickerSymbol: string;
}

export default function Kpis({ tickerSymbol }: KpisProps) {
  const intl = useIntl();
  const [selectedKpis, setSelectedKpis] = useState<string[]>([]);
  const [plotType, setPlotType] = useState<"area" | "column" | "table">("table");
  const [statementType, setStatementType] = useState<IFinancialsStatementType>(IFinancialsStatementType.Annual);
  const { data, loading } = useQuery<IKpisQuery, IKpisQueryVariables>(KpisQuery, {
    variables: {
      tickerSymbol,
    },
  });

  const isAnnual = statementType === IFinancialsStatementType.Annual;
  const isQuarterly = statementType === IFinancialsStatementType.Quarterly;
  const reportDates = (isAnnual ? data?.kpis?.reportDates.annual : data?.kpis?.reportDates.quarterly) || [];
  const kpis = (isAnnual ? data?.kpis?.annual : data?.kpis?.quarterly) || [];
  const percentageFormatter = new Intl.NumberFormat("en-US", {
    style: "percent",
    maximumFractionDigits: 2,
  });
  const moneyFormatter = new Intl.NumberFormat("en-US", {
    compactDisplay: "short",
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  });
  const sortedKpis = orderBy(kpis, ["isImportant", "name"], ["desc", "asc"]).filter((kpi) => {
    return kpi.series.some((value) => value);
  });

  if (loading) {
    return <LoadingSkeleton count={1} className="h-56" />;
  }

  if (kpis.length === 0) {
    return <div>Sem KPIs</div>;
  }

  return (
    <div className="h-full w-full space-y-4">
      <div className="flex w-full flex-col items-center gap-x-4 gap-y-4 sm:flex-row">
        <div className="w-full sm:w-auto">
          <SelectStatementType
            disabled={false}
            statementType={statementType}
            onChangeStatementType={setStatementType}
          />
        </div>

        <PlotTypeSelector
          onChange={(nextPlotType) => {
            if (plotType === "table" && selectedKpis.length === 0 && sortedKpis.length === 1) {
              startTransition(() => {
                setSelectedKpis([sortedKpis[0].name]);
                setPlotType(nextPlotType);
              });
            } else {
              setPlotType(nextPlotType);
            }
          }}
          value={plotType}
          types={[
            {
              type: "table",
              label: intl.formatMessage({
                id: "HDNcCv",
                defaultMessage: "Tabela",
                description: "Tipo de visualização dos dados",
              }),
              icon: <TableIcon className="h-5 w-5" />,
            },
            {
              type: "area",
              label: intl.formatMessage({
                id: "oaRYWa",
                defaultMessage: "Linha",
                description: "Tipo de visualização do gráfico",
              }),
              icon: <ActivityIcon className="h-5 w-5" />,
            },
            {
              type: "column",
              label: intl.formatMessage({
                id: "hZ073v",
                defaultMessage: "Barra",
                description: "Tipo de visualização do gráfico",
              }),
              icon: <BarChartIcon className="h-5 w-5" />,
            },
          ]}
        />
      </div>

      {plotType !== "table" && sortedKpis.length > 1 && (
        <div className="w-full">
          <ReactSelect
            value={selectedKpis.map((value) => ({
              label: value,
              value,
            }))}
            noOptionsMessage={() => intl.formatMessage({ defaultMessage: "Sem opções", id: "psj49M" })}
            placeholder={intl.formatMessage({ defaultMessage: "Selecione um KPI", id: "DoVjJi" })}
            options={sortedKpis.map((node) => ({
              value: node.name,
              label: node.name,
            }))}
            onChange={(options) => {
              setSelectedKpis(options.map((node) => node.value));
            }}
          />
        </div>
      )}

      {plotType !== "table" && selectedKpis.length > 0 && (
        <KpisChart
          plotType={plotType}
          kpis={selectedKpis.map((selectedKpi) => {
            const kpi = sortedKpis.find((kpi) => kpi.name === selectedKpi);
            if (!kpi) {
              throw new Error("KPI not found");
            }
            return {
              name: kpi.name,
              format: kpi.format,
              isCurrency: kpi.isCurrency,
              data: kpi.series.map((value, index) => {
                return {
                  x: parseISO(reportDates[index]).getTime(),
                  y: value,
                };
              }),
            };
          })}
        />
      )}

      {plotType === "table" && (
        <ScrollArea type="always" orientation="horizontal" className="relative h-full w-full">
          <div className="flex flex-col gap-y-4 pb-4 lg:w-full">
            <div
              className={classNames(
                "relative overflow-auto rounded-xl border border-slate-200",
                "dark:border-slate-950",
              )}
            >
              <table className="w-full select-none divide-y divide-slate-200 dark:divide-slate-700">
                <thead>
                  <tr className="divide-x divide-slate-200 bg-slate-50 text-sm dark:divide-slate-700">
                    <th className="p-2 text-sm">KPI</th>
                    {reportDates.map((reportDate, index) => {
                      const isTTM = isAnnual && data?.kpis?.reportDates.isTrailingTwelveMonths[index];
                      return (
                        <th key={reportDate} className="p-2 text-center font-medium">
                          {isTTM ? "TTM" : lightFormat(parseISO(reportDate), isQuarterly ? "MM/yy" : "MM/yyyy")}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {sortedKpis.map((kpi) => {
                    return (
                      <tr key={kpi.name} className="divide-x divide-slate-200 dark:divide-slate-700">
                        <td
                          className={classNames(
                            "whitespace-nowrap bg-slate-50 p-2 text-sm tracking-wide text-slate-600",
                            {
                              "bg-blue-50 font-medium text-blue-600": kpi.isImportant,
                            },
                          )}
                        >
                          {kpi.name}
                        </td>
                        {kpi.series.map((value, index) => {
                          return (
                            <td
                              // eslint-disable-next-line react/no-array-index-key
                              key={`${kpi.name}/${value}/${index}`}
                              className="min-w-20 p-2 text-center font-mono text-sm"
                            >
                              {kpi.format === "number" && kpi.isCurrency && (
                                <span>{value === null ? "-" : `${moneyFormatter.format(value / 1_000_000)}`}</span>
                              )}
                              {kpi.format === "number" && !kpi.isCurrency && (
                                <span>{value === null ? "-" : `${value / 1_000_000}`}</span>
                              )}
                              {kpi.format === "%" && (
                                <span>
                                  {value === null ? "-" : `${percentageFormatter.format(Number(value / 100))}`}
                                </span>
                              )}
                              {kpi.format === "ratio" && <span>{value === null ? "-" : value}</span>}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </ScrollArea>
      )}
    </div>
  );
}
